import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useRegion, Region } from '../src/contexts/RegionContext';

interface AdminContextType {
    activeRegion: Region;
    setActiveRegion: (region: Region) => void;
    isOwner: boolean;
    isAdmin: boolean;
    regionalFilter: any; // Used for Firestore queries
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const { region: userRegion } = useRegion();

    // For Owners, allow switching. For Admins, lock to their preferredRegion.
    const [activeRegion, setActiveRegionState] = useState<Region>(userRegion);

    const isOwner = currentUser?.role === 'owner';
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'support' || currentUser?.role === 'owner';

    useEffect(() => {
        if (!isOwner && currentUser?.preferredRegion) {
            setActiveRegionState(currentUser.preferredRegion as Region);
        } else if (isOwner) {
            // For Owners, follow the browsing region by default
            // This allows them to see the market they just chose site-wide
            setActiveRegionState(userRegion);
        }
    }, [currentUser, isOwner, userRegion]);

    const setActiveRegion = (region: Region) => {
        if (isOwner) {
            setActiveRegionState(region);
        }
    };

    // Helper for Firestore queries: where('marketRegion', '==', activeRegion)
    const regionalFilter = {
        field: 'marketRegion',
        value: activeRegion
    };

    return (
        <AdminContext.Provider value={{
            activeRegion,
            setActiveRegion,
            isOwner,
            isAdmin,
            regionalFilter
        }}>
            {children}
        </AdminContext.Provider>
    );
};

export const useAdmin = () => {
    const context = useContext(AdminContext);
    if (!context) {
        throw new Error('useAdmin must be used within AdminProvider');
    }
    return context;
};
